import { useCallback, useMemo } from 'react';
import { useAcademyContextStore } from '../store/academyContext';
import {
  useUserAcademies,
  useAcademy,
  useUpdateAcademy,
  fetchUserAcademiesQuery,
  fetchAcademyQuery,
  getUserAcademiesFromCache,
  getAcademyFromCache,
  updateAcademyQuery,
} from '../queries/academy';
import {
  useLessons as useLessonsQuery,
  useCourts as useCourtsQuery,
  useCoaches as useCoachesQuery,
  useTerms as useTermsQuery,
  useBookings as useBookingsQuery,
  useAcademyBookingsPage as useAcademyBookingsPageQuery,
  useAcademyStudentsPage as useAcademyStudentsPageQuery,
  useAcademyStudentStats as useAcademyStudentStatsQuery,
  usePrograms as useProgramsQuery,
  useAgeGroups as useAgeGroupsQuery,
  useLocations as useLocationsQuery,
  usePublicLocations as usePublicLocationsQuery,
} from '../queries';
import type { LessonListFilters } from '../queries/keys';
import type { Academy } from '@court-sync/shared';

export { programsFetchKey } from '../queries';
export {
  fetchUserAcademiesQuery,
  fetchAcademyQuery,
  getUserAcademiesFromCache,
  getAcademyFromCache,
  updateAcademyQuery,
};

/**
 * Hook to access and manage lessons (React Query).
 */
export function useLessons(academyId?: string, listFilters?: LessonListFilters) {
  return useLessonsQuery(academyId, listFilters);
}

/**
 * Hook to access and manage courts (React Query).
 */
export function useCourts(academyId?: string) {
  return useCourtsQuery(academyId);
}

/**
 * Hook to access and manage coaches (React Query).
 */
export function useCoaches(academyId?: string) {
  return useCoachesQuery(academyId);
}

/**
 * Coach invite status and per-coach stats (React Query).
 */
export { useCoachInviteStatus, useCoachStatsForAcademy } from '../queries/coaches';
export type { CoachInviteStatus, CoachStats } from '../queries/coaches';

/**
 * Academy locations (React Query).
 */
export function useLocations(
  academyId?: string,
  options?: { includeInactive?: boolean; enabled?: boolean }
) {
  return useLocationsQuery(academyId, options);
}

/** Active locations for coach-facing schedule views. */
export function usePublicLocations(academyId?: string, options?: { enabled?: boolean }) {
  return usePublicLocationsQuery(academyId, options);
}

/**
 * Hook to access and manage programs (React Query).
 */
export function usePrograms(
  academyId?: string,
  options?: { includeInactive?: boolean; enabled?: boolean }
) {
  return useProgramsQuery(academyId, options);
}

/**
 * Hook to access and manage age groups (React Query).
 */
export function useAgeGroups(academyId?: string, options?: { enabled?: boolean }) {
  return useAgeGroupsQuery(academyId, options);
}

/**
 * Hook to access and manage terms (React Query).
 */
export function useTerms(academyId?: string) {
  return useTermsQuery(academyId);
}

/**
 * Hook to access and manage bookings (React Query).
 */
export function useBookings(filters?: {
  academyId?: string;
  lessonId?: string;
  customerId?: string;
}) {
  return useBookingsQuery(filters);
}

/**
 * Paginated academy bookings list (owner dashboard).
 */
export function useAcademyBookingsPage(
  academyId?: string,
  page?: number,
  filters?: import('../queries/bookings').AcademyBookingsPageFilters,
  options?: { enabled?: boolean }
) {
  return useAcademyBookingsPageQuery(academyId, page, filters, options);
}

/**
 * Paginated academy students list + attendance stats.
 */
export function useAcademyStudentsPage(
  academyId?: string,
  page?: number,
  filters?: import('@court-sync/shared').StudentFilters,
  options?: { enabled?: boolean }
) {
  return useAcademyStudentsPageQuery(academyId, page, filters, options);
}

export function useAcademyStudentStats(academyId?: string, options?: { enabled?: boolean }) {
  return useAcademyStudentStatsQuery(academyId, options);
}

/**
 * Resolves the currently selected academy from React Query cache + detail query.
 */
export function useCurrentAcademy() {
  const selectedAcademyId = useAcademyContextStore((s) => s.selectedAcademyId);
  const selectAcademy = useAcademyContextStore((s) => s.selectAcademy);
  const { academies, isLoading: listLoading, error: listError, refresh: refreshList } =
    useUserAcademies();
  const {
    academy: detailAcademy,
    isLoading: detailLoading,
    error: detailError,
    refresh: refreshDetail,
  } = useAcademy(selectedAcademyId);
  const updateMutation = useUpdateAcademy();

  const currentAcademy = useMemo((): Academy | null => {
    if (!selectedAcademyId) return null;
    if (detailAcademy) return detailAcademy;
    return academies.find((a) => a.id === selectedAcademyId) ?? null;
  }, [selectedAcademyId, detailAcademy, academies]);

  const setCurrentAcademy = useCallback(
    (academy: Academy | null) => selectAcademy(academy),
    [selectAcademy]
  );

  const loadCurrentAcademy = useCallback(
    async (academyId: string, options?: { force?: boolean }) => {
      const academy = await fetchAcademyQuery(academyId, options);
      if (academy) {
        selectAcademy(academy);
      }
    },
    [selectAcademy]
  );

  const updateAcademy = useCallback(
    async (id: string, data: Partial<Omit<Academy, 'id' | 'createdAt' | 'updatedAt'>>) => {
      const updated = await updateMutation.mutateAsync({ id, data });
      if (updated && selectedAcademyId === id) {
        selectAcademy(updated);
      }
      return updated;
    },
    [updateMutation, selectedAcademyId, selectAcademy]
  );

  return {
    currentAcademy,
    userAcademies: academies,
    isLoading: listLoading || (!!selectedAcademyId && detailLoading),
    error: listError ?? detailError,
    setCurrentAcademy,
    loadCurrentAcademy,
    loadUserAcademies: refreshList,
    updateAcademy,
    refreshDetail,
  };
}

/**
 * Hook to access and manage academies (React Query + client selection context).
 */
export function useAcademies() {
  const {
    currentAcademy,
    userAcademies,
    isLoading,
    error,
    setCurrentAcademy,
    loadCurrentAcademy,
    loadUserAcademies,
    updateAcademy,
  } = useCurrentAcademy();

  return {
    academies: userAcademies,
    currentAcademy,
    isLoading,
    error,
    refresh: () => loadUserAcademies({ force: true }),
    loadAcademy: loadCurrentAcademy,
    setCurrentAcademy,
    updateAcademy,
  };
}
